// Async job client for the heavy endpoints (/api/compare, /api/analyze).
//
// Flow (keeps large files off the API Gateway 10 MB request path):
//   1. POST small JSON {params, files:{field:filename}} -> {jobId, uploads:{field:url}}
//   2. PUT each file DIRECTLY to its presigned S3 URL (no gateway/Lambda in the path)
//   3. POST /api/jobs/{jobId}/start  -> enqueues the worker
//   4. poll /api/result/{jobId} until done, then download from a presigned S3 URL
//
// See backend/jobs + backend/handlers/async.go for the server side.

type JobStatus =
  | { state: 'pending' }
  | { state: 'error'; message?: string }
  | { state: 'done'; url: string; filename?: string };

interface SubmitOptions {
  intervalMs?: number;
  timeoutMs?: number;
}

/**
 * submitJob creates a job, uploads its files directly to S3, starts it, polls
 * until it finishes, then downloads the result. Throws on failure or timeout so
 * callers keep their existing try/catch handling.
 *
 * @param endpoint  /api/compare or /api/analyze
 * @param files     field name (sheetA/sheetB/sheet) -> File
 * @param params    non-file params (e.g. { mappings } or { script })
 */
export async function submitJob(
  endpoint: string,
  files: Record<string, File>,
  params: Record<string, string>,
  fallbackFilename = 'result.xlsx',
  opts: SubmitOptions = {},
): Promise<void> {
  const intervalMs = opts.intervalMs ?? 2000;
  const timeoutMs = opts.timeoutMs ?? 15 * 60 * 1000; // matches the Lambda max

  // 1. create the job + get presigned upload URLs
  const filenames = Object.fromEntries(
    Object.entries(files).map(([field, file]) => [field, file.name]),
  );
  const created = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...params, files: filenames }),
  });
  if (!created.ok) {
    throw new Error(`HTTP ${created.status} - ${await created.text()}`);
  }
  const { jobId, uploads } = await created.json();
  if (!jobId || !uploads) throw new Error('Server did not return upload URLs');

  // 2. upload each file straight to S3 (no size limit)
  await Promise.all(
    Object.entries(files).map(async ([field, file]) => {
      const put = await fetch(uploads[field], { method: 'PUT', body: file });
      if (!put.ok) throw new Error(`Upload of ${field} failed (HTTP ${put.status})`);
    }),
  );

  // 3. start processing
  const started = await fetch(`/api/jobs/${jobId}/start`, { method: 'POST' });
  if (!started.ok) {
    throw new Error(`HTTP ${started.status} - ${await started.text()}`);
  }

  // 4. poll then download
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await sleep(intervalMs);

    const res = await fetch(`/api/result/${jobId}`);
    if (!res.ok) continue; // transient — keep polling

    const status: JobStatus = await res.json();
    if (status.state === 'done') {
      await downloadFile(status.url, status.filename || fallbackFilename);
      return;
    }
    if (status.state === 'error') {
      throw new Error(status.message || 'The job failed.');
    }
  }
  throw new Error('Timed out waiting for the result.');
}

async function downloadFile(url: string, filename: string): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to download result: HTTP ${res.status}`);
  const blob = await res.blob();

  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
